import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { BRAND } from "@/lib/dotecApi";
import { useLang } from "@/i18n/LangContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { FileSpreadsheet, FileText, BarChart3, Menu, X, PenSquare, Camera, HelpCircle, FileArchive, BookOpen } from "lucide-react";

const ANALYSIS_LABEL = { it: "Analisi Excel", en: "Excel Analysis", es: "Análisis Excel", fr: "Analyse Excel", de: "Excel-Analyse" };
const EDIT_LABEL = { it: "Modifica PDF", en: "Edit PDF", es: "Editar PDF", fr: "Modifier PDF", de: "PDF bearbeiten" };
const IMAGE_LABEL = { it: "Immagine → PDF", en: "Image → PDF", es: "Imagen → PDF", fr: "Image → PDF", de: "Bild → PDF" };
const COMPRESS_LABEL = { it: "Comprimi", en: "Compress", es: "Comprimir", fr: "Compresser", de: "Komprimieren" };
const TAGLINE = { it: "Convertitore PDF professionale", en: "Professional PDF converter", es: "Conversor PDF profesional", fr: "Convertisseur PDF professionnel", de: "Professioneller PDF-Konverter" };

export const Header = () => {
  const location = useLocation();
  const { t, lang } = useLang();
  const [open, setOpen] = useState(false);

  const NAV = [
    { to: "/pdf-excel", label: "PDF → Excel", icon: FileSpreadsheet },
    { to: "/pdf-word", label: "PDF → Word", icon: FileText },
    { to: "/immagine-pdf", label: IMAGE_LABEL[lang] || IMAGE_LABEL.it, icon: Camera },
    { to: "/analisi-excel", label: ANALYSIS_LABEL[lang] || ANALYSIS_LABEL.it, icon: BarChart3 },
    { to: "/modifica-pdf", label: EDIT_LABEL[lang] || EDIT_LABEL.it, icon: PenSquare },
    { to: "/comprimi-pdf", label: COMPRESS_LABEL[lang] || COMPRESS_LABEL.it, icon: FileArchive },
    { to: "/faq", label: "FAQ", icon: HelpCircle },
    { to: "/guide", label: "Guide", icon: BookOpen },
  ];

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 w-full border-b border-slate-200 bg-white/85 backdrop-blur-xl"
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 lg:px-6">
        <Link
          to="/"
          data-testid="brand-logo-link"
          className="flex shrink-0 items-center gap-3 transition-opacity hover:opacity-80"
        >
          <img
            src={BRAND.logo}
            alt="Dotec EW-PDF"
            className="h-10 w-10 shrink-0 rounded-xl object-cover shadow-sm"
          />
          <div className="flex flex-col justify-center">
            <span className="whitespace-nowrap font-display text-lg font-extrabold leading-tight tracking-tight text-slate-900">
              Dotec <span className="text-emerald-600">EW-PDF</span>
            </span>
            <p className="hidden whitespace-nowrap text-[11px] font-medium leading-tight text-slate-400 xl:block">
              {TAGLINE[lang] || TAGLINE.it}
            </p>
          </div>
        </Link>

        <nav className="hidden items-center gap-0.5 lg:flex">
          {NAV.map((item) => {
            const active = location.pathname === item.to;
            return (
              <Link
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.to.slice(1)}`}
                className={`flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2 py-2 text-[13px] font-semibold transition-colors xl:px-2.5 ${
                  active
                    ? "bg-emerald-50 text-emerald-700"
                    : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`}
              >
                <item.icon className="hidden h-4 w-4 2xl:block" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="hidden shrink-0 items-center gap-2 lg:flex">
          <LanguageSwitcher variant="desktop" />
          <Link
            to="/pdf-excel"
            data-testid="header-cta-button"
            className="hidden whitespace-nowrap rounded-xl bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-sm transition-colors hover:bg-emerald-700 xl:block"
          >
            {t.convertNow}
          </Link>
        </div>

        <div className="flex items-center gap-2 lg:hidden">
          <LanguageSwitcher variant="mobile" />
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            className="rounded-lg p-2 text-slate-700"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <nav className="border-t border-slate-200 bg-white px-6 py-3 lg:hidden">
          {NAV.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`mobile-nav-${item.to.slice(1)}`}
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-semibold text-slate-700 hover:bg-slate-100"
            >
              <item.icon className="h-5 w-5 text-emerald-600" />
              {item.label}
            </Link>
          ))}
        </nav>
      )}
    </header>
  );
};
